import { useEffect, useRef } from "react";
import useSWR from 'swr';
import { LocationsClient, OptionsSource, RwPayer } from "../../../api/web-api-client";
import { usePatientAppContext } from "../../../context/patient-app-context";
import clientService from "../../../services/clientService";
import IComponent from "../../../services/forms.interface";
import Keys from "../../../services/keys";
import ID from "../../shared/UUID";
import { AUTOCOMPLETE, INSURANCE } from "../shared/constants";
import { ActionKind } from "./form-view-reducer";

export function usePayerEffects(state, dispatch, locationId: number | null) {
    const { unwrapperedDomain } = usePatientAppContext();

    const { data: payerList } = useSWR(state.loadPayerList && locationId ? [Keys.ApiLocationPayers, locationId] : null, (url, id) => payerfetcher(id));

    const payerfetcher = async (id) => {
        const client = clientService.getFormsClient(LocationsClient, { apiPath: unwrapperedDomain });
        const query = await client.listPayers(id);
        return query;
    };

    const getAllPayerComponents = (components: Record<string, IComponent>): IComponent[] => {
        const result: IComponent[] = [];
        Object.values(components).forEach((comp: IComponent) => {
            // Collect AUTOCOMPLETE components
            if (comp.type === AUTOCOMPLETE && comp.optionsSource === OptionsSource.INSURANCEPROVIDER) {
                result.push(comp);
            }
            // Collect INSURANCE components with child AUTOCOMPLETE components
            if (comp.type === INSURANCE && comp.childComponents) {
                const hasAutocompleteChild = Object.values(comp.childComponents).some(
                    (child: IComponent) => child.type === AUTOCOMPLETE && child.optionsSource === OptionsSource.INSURANCEPROVIDER
                );
                if (hasAutocompleteChild) {
                    result.push(comp);
                }
            }
        });
        return result;
    };

    useEffect(() => {
        
        if (payerList) {
            
            const payerComponents = getAllPayerComponents(state.data.json?.components);

            // Flatten options for each payer and their alternate names/ids
            const options = payerList.flatMap((payer: RwPayer) => {
                const mainOption = {
                    key: `${ID.uuid()}`, // main id, no alternate id
                    value: payer.name,
                    text: payer.name
                };

                // Alternate names
                const alternateOptions = (payer.alternateNames || []).map((altName, idx) => ({
                    key: `${ID.uuid()}`,
                    value: altName,
                    text: altName
                }));

                return [mainOption, ...alternateOptions];
            });

            // order options by text
            options.sort((a, b) => a.text.localeCompare(b.text));

            payerComponents.forEach((payer: IComponent) => {
                if (payer.type === INSURANCE && payer.childComponents) {
                    // Find the AUTOCOMPLETE child and set its options
                    const autocompleteChild = Object.values(payer.childComponents).find(
                        (child: IComponent) => child.type === AUTOCOMPLETE && child.optionsSource === OptionsSource.INSURANCEPROVIDER
                    );
                    if (autocompleteChild) {
                        autocompleteChild.options = options;
                    }
                } else {
                    // For top-level AUTOCOMPLETE components, set options directly
                    payer.options = options;
                }
                state.data.json.components[payer.id] = payer;
            });

            dispatch({ type: ActionKind.SET_COMPONENTS, payload: state.data.json.components });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payerList]);

    // Track if payer list has been loaded for this form
    const payerListLoadedRef = useRef(false);

    const hasPayerComponent = (components: Record<string, IComponent>): boolean => {
        return Object.values(components).some((comp: IComponent) => {
            if (comp.type === AUTOCOMPLETE && comp.optionsSource === OptionsSource.INSURANCEPROVIDER) {
                return true;
            }
            if (comp.type === INSURANCE && comp.childComponents) {
                return Object.values(comp.childComponents).some(
                    (child: IComponent) => child.type === AUTOCOMPLETE && child.optionsSource === OptionsSource.INSURANCEPROVIDER
                );
            }
            return false;
        });
    };

    useEffect(() => {

        if (payerListLoadedRef.current || !state.data?.json?.components)
            return;

        if (!hasPayerComponent(state.data.json.components))
            return;

        if (!locationId)
            return;

        dispatch({ type: ActionKind.SET_LOADPAYERLIST, payload: true });

        payerListLoadedRef.current = true;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.data?.json?.components, locationId]);
}


